/**
 * usePokemonTCG
 * Fetches live card data from api.pokemontcg.io v2.
 * Results are cached in localStorage (6 h TTL) so the pool stays stable
 * within a session while staying fresh daily.
 *
 * The pool is sorted deterministically by card ID so that on-chain seeds
 * always map to the same card regardless of fetch order.
 */

import { useEffect, useState } from "react";

export type CardRarity = "common" | "uncommon" | "rare" | "holo" | "ultra" | "graded";

export interface TcgPrice {
  low: number | null;
  mid: number | null;
  high: number | null;
  market: number | null;
}

export interface LiveCard {
  id: string;
  name: string;
  setId: string;
  setName: string;
  number: string;
  rarity: CardRarity;
  imageSmall: string;
  imageLarge: string;
  approxValueUsd: number;
  /** True if this is a PSA/BGS/CGC graded slab */
  isGraded: boolean;
  gradingCompany?: "PSA" | "BGS" | "CGC";
  grade?: number;
  /** Set to true for Illustration Rares, Secret Rares, Alt Arts, etc. */
  isAltArt: boolean;
}

// ── Internal API shape ─────────────────────────────────────────────────────────
interface ApiCard {
  id: string;
  name: string;
  set: { id: string; name: string };
  number: string;
  rarity: string;
  images: { small: string; large: string };
  tcgplayer?: { prices?: Record<string, TcgPrice> };
}

// ── Constants ─────────────────────────────────────────────────────────────────
const BASE = "https://api.pokemontcg.io/v2";
const POKEWALLET_BASE = "https://api.pokewallet.io";
const POKEWALLET_KEY  = import.meta.env.VITE_POKEWALLET_API_KEY ?? "";
const CACHE_KEY = "ptcg_pool_v4";
const CACHE_TTL = 2 * 60 * 60 * 1000; // 2 hours — keeps packs dynamic

// All sets the pool rotates through — large spread for variety
const SETS = [
  "sv3pt5",    // 151 (iconic modern)
  "sv4",       // Paradox Rift
  "sv5",       // Temporal Forces
  "sv6",       // Twilight Masquerade
  "sv4pt5",    // Paldean Fates
  "sv7",       // Stellar Crown
  "swsh9",     // Evolving Skies
  "swsh10",    // Silver Tempest (Lugia)
  "swsh11",    // Lost Origin
  "swsh12",    // Silver Tempest base
  "swsh12pt5", // Crown Zenith
  "base1",     // Base Set (Charizard, Blastoise, Mewtwo)
  "base2",     // Jungle
  "base3",     // Fossil
  "neo1",      // Neo Genesis (Lugia)
  "neo2",      // Neo Discovery
  "sm12",      // Cosmic Eclipse
  "sm11",      // Unified Minds
  "xy7",       // Ancient Origins
  "det1",      // Detective Pikachu
];

// Map TCG API rarity string → our internal rarity bucket
function mapRarity(tcgRarity: string, setId: string): CardRarity {
  const r = tcgRarity.toLowerCase();
  if (r.includes("special illustration") || r.includes("hyper rare")) return "ultra";
  if (r.includes("rare secret") || r.includes("rare rainbow"))        return "ultra";
  if (r.includes("rare holo vmax") || r.includes("rare holo vstar"))  return "ultra";
  if (r.includes("illustration rare"))                                 return "holo";
  if (r.includes("rare holo"))                                         return "holo";
  if (r.includes("trainer gallery"))                                   return "holo";
  if (r.includes("rare"))                                              return "rare";
  if (r.includes("uncommon"))                                          return "uncommon";
  return "common";
}

function extractPrice(card: ApiCard): number {
  if (!card.tcgplayer?.prices) return 1;
  const p = card.tcgplayer.prices;
  const val =
    p["holofoil"]?.market  ??
    p["normal"]?.market    ??
    p["reverseHolofoil"]?.market ??
    p["1stEditionHolofoil"]?.market ??
    Object.values(p)[0]?.market ??
    0;
  return Math.round(val * 100) / 100 || 1;
}

function isAltArt(tcgRarity: string): boolean {
  const r = tcgRarity.toLowerCase();
  return r.includes("special illustration") || r.includes("illustration rare") || r.includes("hyper rare");
}

function toSetQuery(sets: string[]): string {
  return sets.map((s) => `set.id:${s}`).join(" OR ");
}

// Enrich a card's price using pokewallet.io real-time data (best-effort)
async function enrichPriceFromPokewallet(name: string): Promise<number | null> {
  if (!POKEWALLET_KEY) return null;
  try {
    const res = await fetch(
      `${POKEWALLET_BASE}/search?q=${encodeURIComponent(name)}&limit=5`,
      { headers: { "X-API-Key": POKEWALLET_KEY } }
    );
    if (!res.ok) return null;
    const json = await res.json();
    const results: any[] = json.results ?? [];
    if (!results.length) return null;
    // Pick highest market price from TCGPlayer across results
    let best = 0;
    for (const r of results) {
      for (const p of (r.tcgplayer?.prices ?? [])) {
        if (p.market_price && p.market_price > best) best = p.market_price;
      }
    }
    return best > 0 ? Math.round(best * 100) / 100 : null;
  } catch {
    return null;
  }
}

async function fetchPage(q: string, page: number, pageSize = 100): Promise<{ data: ApiCard[]; totalCount: number }> {
  const params = new URLSearchParams({ q, page: String(page), pageSize: String(pageSize), select: "id,name,set,number,rarity,images,tcgplayer" });
  const res = await fetch(`${BASE}/cards?${params}`);
  if (!res.ok) throw new Error(`TCG API error ${res.status}`);
  const json = await res.json();
  return { data: json.data ?? [], totalCount: json.totalCount ?? 0 };
}

async function fetchRarityBucket(rarities: string[], maxCards = 200): Promise<ApiCard[]> {
  const rarityQ = rarities.map((r) => `rarity:"${r}"`).join(" OR ");
  const setQ    = toSetQuery(SETS);
  const q = `(${rarityQ}) (${setQ})`;

  const first = await fetchPage(q, 1, 100);
  const results = [...first.data];

  if (first.totalCount > 100 && results.length < maxCards) {
    const page2 = await fetchPage(q, 2, 100);
    results.push(...page2.data);
  }

  return results.slice(0, maxCards);
}

function apiCardToLive(api: ApiCard): LiveCard {
  const rarity = mapRarity(api.rarity, api.set.id);
  return {
    id: api.id,
    name: api.name,
    setId: api.set.id,
    setName: api.set.name,
    number: api.number,
    rarity,
    imageSmall: api.images.small,
    imageLarge: api.images.large,
    approxValueUsd: extractPrice(api),
    isGraded: false,
    isAltArt: isAltArt(api.rarity),
  };
}

// ── Graded (slab) card pool ───────────────────────────────────────────────────
// These are real PSA/BGS graded copies with verified grades + approx prices.
// Physical redemption only — can't be sold back for crypto.
export const GRADED_POOL: LiveCard[] = [
  { id: "g_charizard_b1_psa10",   name: "Charizard (Base Set)",        setId: "base1",      setName: "Base Set",          number: "4",   rarity: "graded", imageSmall: "https://images.pokemontcg.io/base1/4.png",       imageLarge: "https://images.pokemontcg.io/base1/4_hires.png",       approxValueUsd: 12000,  isGraded: true, gradingCompany: "PSA", grade: 10, isAltArt: false },
  { id: "g_charizard_b1_psa9",    name: "Charizard (Base Set)",        setId: "base1",      setName: "Base Set",          number: "4",   rarity: "graded", imageSmall: "https://images.pokemontcg.io/base1/4.png",       imageLarge: "https://images.pokemontcg.io/base1/4_hires.png",       approxValueUsd: 3500,   isGraded: true, gradingCompany: "PSA", grade: 9,  isAltArt: false },
  { id: "g_charizard_b1_1st_psa9",name: "Charizard 1st Ed. (Base)",    setId: "base1",      setName: "Base Set 1st Ed.",  number: "4",   rarity: "graded", imageSmall: "https://images.pokemontcg.io/base1/4.png",       imageLarge: "https://images.pokemontcg.io/base1/4_hires.png",       approxValueUsd: 35000,  isGraded: true, gradingCompany: "PSA", grade: 9,  isAltArt: false },
  { id: "g_mewtwo_b1_psa10",      name: "Mewtwo (Base Set)",           setId: "base1",      setName: "Base Set",          number: "10",  rarity: "graded", imageSmall: "https://images.pokemontcg.io/base1/10.png",      imageLarge: "https://images.pokemontcg.io/base1/10_hires.png",      approxValueUsd: 1800,   isGraded: true, gradingCompany: "PSA", grade: 10, isAltArt: false },
  { id: "g_lugia_neo1_psa10",     name: "Lugia (Neo Genesis)",         setId: "neo1",       setName: "Neo Genesis",       number: "9",   rarity: "graded", imageSmall: "https://images.pokemontcg.io/neo1/9.png",        imageLarge: "https://images.pokemontcg.io/neo1/9_hires.png",        approxValueUsd: 9000,   isGraded: true, gradingCompany: "PSA", grade: 10, isAltArt: false },
  { id: "g_lugia_neo1_psa9",      name: "Lugia (Neo Genesis)",         setId: "neo1",       setName: "Neo Genesis",       number: "9",   rarity: "graded", imageSmall: "https://images.pokemontcg.io/neo1/9.png",        imageLarge: "https://images.pokemontcg.io/neo1/9_hires.png",        approxValueUsd: 2500,   isGraded: true, gradingCompany: "PSA", grade: 9,  isAltArt: false },
  { id: "g_umbreon_swsh9_psa10",  name: "Umbreon VMAX Alt Art",        setId: "swsh9",      setName: "Evolving Skies",    number: "215", rarity: "graded", imageSmall: "https://images.pokemontcg.io/swsh9/215.png",      imageLarge: "https://images.pokemontcg.io/swsh9/215_hires.png",     approxValueUsd: 950,    isGraded: true, gradingCompany: "PSA", grade: 10, isAltArt: true  },
  { id: "g_charizard_sir_psa10",  name: "Charizard ex SAR (151)",      setId: "sv3pt5",     setName: "151",               number: "206", rarity: "graded", imageSmall: "https://images.pokemontcg.io/sv3pt5/206.png",     imageLarge: "https://images.pokemontcg.io/sv3pt5/206_hires.png",    approxValueUsd: 1400,   isGraded: true, gradingCompany: "PSA", grade: 10, isAltArt: true  },
  { id: "g_pikachu_sir_psa10",    name: "Pikachu ex SAR (151)",        setId: "sv3pt5",     setName: "151",               number: "207", rarity: "graded", imageSmall: "https://images.pokemontcg.io/sv3pt5/207.png",     imageLarge: "https://images.pokemontcg.io/sv3pt5/207_hires.png",    approxValueUsd: 450,    isGraded: true, gradingCompany: "PSA", grade: 10, isAltArt: true  },
  { id: "g_mew_psa10",            name: "Mew VMAX Alt Art",            setId: "swsh12pt5",  setName: "Crown Zenith",      number: "GG55",rarity: "graded", imageSmall: "https://images.pokemontcg.io/swsh12pt5/GG55.png", imageLarge: "https://images.pokemontcg.io/swsh12pt5/GG55_hires.png",approxValueUsd: 280,    isGraded: true, gradingCompany: "PSA", grade: 10, isAltArt: true  },
  { id: "g_lugia_vstar_psa10",    name: "Lugia VSTAR Alt Art",         setId: "swsh10",     setName: "Silver Tempest",    number: "196", rarity: "graded", imageSmall: "https://images.pokemontcg.io/swsh10/196.png",     imageLarge: "https://images.pokemontcg.io/swsh10/196_hires.png",    approxValueUsd: 500,    isGraded: true, gradingCompany: "PSA", grade: 10, isAltArt: true  },
  { id: "g_rayquaza_swsh9_psa10", name: "Rayquaza VMAX Alt Art",       setId: "swsh9",      setName: "Evolving Skies",    number: "218", rarity: "graded", imageSmall: "https://images.pokemontcg.io/swsh9/218.png",      imageLarge: "https://images.pokemontcg.io/swsh9/218_hires.png",     approxValueUsd: 850,    isGraded: true, gradingCompany: "PSA", grade: 10, isAltArt: true  },
  { id: "g_zapdos_b1_psa10",      name: "Zapdos (Base Set)",           setId: "base1",      setName: "Base Set",          number: "16",  rarity: "graded", imageSmall: "https://images.pokemontcg.io/base1/16.png",      imageLarge: "https://images.pokemontcg.io/base1/16_hires.png",      approxValueUsd: 2200,   isGraded: true, gradingCompany: "PSA", grade: 10, isAltArt: false },
  { id: "g_gyarados_b1_psa10",    name: "Gyarados (Base Set)",         setId: "base1",      setName: "Base Set",          number: "6",   rarity: "graded", imageSmall: "https://images.pokemontcg.io/base1/6.png",       imageLarge: "https://images.pokemontcg.io/base1/6_hires.png",       approxValueUsd: 1600,   isGraded: true, gradingCompany: "BGS", grade: 10, isAltArt: false },
  { id: "g_ogerpon_psa10",        name: "Ogerpon ex SAR",              setId: "sv4pt5",     setName: "Paldean Fates",     number: "232", rarity: "graded", imageSmall: "https://images.pokemontcg.io/sv4pt5/232.png",     imageLarge: "https://images.pokemontcg.io/sv4pt5/232_hires.png",    approxValueUsd: 380,    isGraded: true, gradingCompany: "PSA", grade: 10, isAltArt: true  },
];

// ── Pool type ─────────────────────────────────────────────────────────────────
export interface CardPool {
  common:   LiveCard[];
  uncommon: LiveCard[];
  rare:     LiveCard[];
  holo:     LiveCard[];
  ultra:    LiveCard[];
  graded:   LiveCard[];
  fetchedAt: number;
}

// ── Hardcoded fallback pool (used when API is unreachable) ────────────────────
// Minimal set that guarantees the feature always works offline
const FALLBACK_POOL: Omit<CardPool, "fetchedAt"> = {
  common:   [
    { id: "base1-65", name: "Squirtle",   setId: "base1", setName: "Base Set", number: "65", rarity: "common",   imageSmall: "https://images.pokemontcg.io/base1/65.png", imageLarge: "https://images.pokemontcg.io/base1/65_hires.png", approxValueUsd: 25, isGraded: false, isAltArt: false },
    { id: "base1-46", name: "Charmander", setId: "base1", setName: "Base Set", number: "46", rarity: "common",   imageSmall: "https://images.pokemontcg.io/base1/46.png", imageLarge: "https://images.pokemontcg.io/base1/46_hires.png", approxValueUsd: 8,  isGraded: false, isAltArt: false },
    { id: "base1-57", name: "Magikarp",   setId: "base1", setName: "Base Set", number: "57", rarity: "common",   imageSmall: "https://images.pokemontcg.io/base1/57.png", imageLarge: "https://images.pokemontcg.io/base1/57_hires.png", approxValueUsd: 18, isGraded: false, isAltArt: false },
    { id: "base1-59", name: "Onix",       setId: "base1", setName: "Base Set", number: "59", rarity: "common",   imageSmall: "https://images.pokemontcg.io/base1/59.png", imageLarge: "https://images.pokemontcg.io/base1/59_hires.png", approxValueUsd: 10, isGraded: false, isAltArt: false },
  ],
  uncommon: [
    { id: "base1-28", name: "Scyther",    setId: "base1", setName: "Base Set", number: "28", rarity: "uncommon", imageSmall: "https://images.pokemontcg.io/base1/28.png", imageLarge: "https://images.pokemontcg.io/base1/28_hires.png", approxValueUsd: 20, isGraded: false, isAltArt: false },
    { id: "base1-36", name: "Clefairy",   setId: "base1", setName: "Base Set", number: "36", rarity: "uncommon", imageSmall: "https://images.pokemontcg.io/base1/36.png", imageLarge: "https://images.pokemontcg.io/base1/36_hires.png", approxValueUsd: 12, isGraded: false, isAltArt: false },
  ],
  rare: [
    { id: "base1-2",  name: "Blastoise",  setId: "base1", setName: "Base Set", number: "2",  rarity: "rare",     imageSmall: "https://images.pokemontcg.io/base1/2.png",  imageLarge: "https://images.pokemontcg.io/base1/2_hires.png",  approxValueUsd: 80, isGraded: false, isAltArt: false },
    { id: "base1-15", name: "Venusaur",   setId: "base1", setName: "Base Set", number: "15", rarity: "rare",     imageSmall: "https://images.pokemontcg.io/base1/15.png", imageLarge: "https://images.pokemontcg.io/base1/15_hires.png", approxValueUsd: 65, isGraded: false, isAltArt: false },
  ],
  holo: [
    { id: "base1-4",  name: "Charizard",  setId: "base1", setName: "Base Set", number: "4",  rarity: "holo",     imageSmall: "https://images.pokemontcg.io/base1/4.png",  imageLarge: "https://images.pokemontcg.io/base1/4_hires.png",  approxValueUsd: 400, isGraded: false, isAltArt: false },
    { id: "base1-10", name: "Mewtwo",     setId: "base1", setName: "Base Set", number: "10", rarity: "holo",     imageSmall: "https://images.pokemontcg.io/base1/10.png", imageLarge: "https://images.pokemontcg.io/base1/10_hires.png", approxValueUsd: 200, isGraded: false, isAltArt: false },
    { id: "base1-6",  name: "Gyarados",   setId: "base1", setName: "Base Set", number: "6",  rarity: "holo",     imageSmall: "https://images.pokemontcg.io/base1/6.png",  imageLarge: "https://images.pokemontcg.io/base1/6_hires.png",  approxValueUsd: 180, isGraded: false, isAltArt: false },
  ],
  ultra: [
    { id: "swsh9-215",  name: "Umbreon VMAX Alt Art",  setId: "swsh9",  setName: "Evolving Skies", number: "215", rarity: "ultra", imageSmall: "https://images.pokemontcg.io/swsh9/215.png",  imageLarge: "https://images.pokemontcg.io/swsh9/215_hires.png",  approxValueUsd: 450, isGraded: false, isAltArt: true  },
    { id: "sv3pt5-206", name: "Charizard ex SAR (151)", setId: "sv3pt5", setName: "151",           number: "206", rarity: "ultra", imageSmall: "https://images.pokemontcg.io/sv3pt5/206.png", imageLarge: "https://images.pokemontcg.io/sv3pt5/206_hires.png", approxValueUsd: 600, isGraded: false, isAltArt: true  },
  ],
  graded: GRADED_POOL,
};

// ── Main fetch function ────────────────────────────────────────────────────────
export async function buildLivePool(): Promise<CardPool> {
  // Check cache first
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      const parsed: CardPool = JSON.parse(cached);
      if (Date.now() - parsed.fetchedAt < CACHE_TTL) return parsed;
    }
  } catch {}

  try {
    const [common, uncommon, rare, holo, ultra] = await Promise.all([
      fetchRarityBucket(["Common"],   250),
      fetchRarityBucket(["Uncommon"], 200),
      fetchRarityBucket(["Rare"],     150),
      fetchRarityBucket(["Rare Holo", "Rare Holo V", "Illustration Rare", "Rare Holo EX", "Rare Holo GX", "Trainer Gallery Rare Holo"], 120),
      fetchRarityBucket(["Special Illustration Rare", "Hyper Rare", "Rare Secret", "Rare Rainbow", "Rare Holo VMAX", "Rare Holo VSTAR", "Rare Ultra"], 80),
    ]);

    const toSorted = (arr: ApiCard[]) =>
      [...arr].sort((a, b) => a.id.localeCompare(b.id)).map(apiCardToLive);

    // Enrich graded pool prices from pokewallet (best-effort, fire-and-forget style)
    const gradedEnriched = await Promise.all(
      [...GRADED_POOL].sort((a, b) => a.id.localeCompare(b.id)).map(async (card) => {
        const live = await enrichPriceFromPokewallet(`${card.name} PSA ${card.grade}`);
        return live ? { ...card, approxValueUsd: live } : card;
      })
    );

    const pool: CardPool = {
      common:   toSorted(common),
      uncommon: toSorted(uncommon),
      rare:     toSorted(rare),
      holo:     toSorted(holo),
      ultra:    toSorted(ultra),
      graded:   gradedEnriched,
      fetchedAt: Date.now(),
    };

    localStorage.setItem(CACHE_KEY, JSON.stringify(pool));
    return pool;
  } catch (err) {
    console.warn("[usePokemonTCG] API fetch failed, using fallback pool", err);
    return { ...FALLBACK_POOL, fetchedAt: Date.now() };
  }
}

// ── React hook ────────────────────────────────────────────────────────────────
export interface UsePokemonTCGResult {
  pool: CardPool | null;
  loading: boolean;
  error: string | null;
  totalCards: number;
}

export function usePokemonTCG(): UsePokemonTCGResult {
  const [pool, setPool]       = useState<CardPool | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    buildLivePool()
      .then((p) => { if (!cancelled) { setPool(p); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setError(String(e)); setLoading(false); } });

    return () => { cancelled = true; };
  }, []);

  const totalCards = pool
    ? pool.common.length + pool.uncommon.length + pool.rare.length + pool.holo.length + pool.ultra.length + pool.graded.length
    : 0;

  return { pool, loading, error, totalCards };
}
